import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { qk } from "../../api/queries";
import type { AgentPoolType } from "../../api/types";
import { useDialogs } from "../../components/Dialogs";
import { ErrorBox, Spinner } from "../../components/Spinner";

export function AgentPoolSettings() {
  const qc = useQueryClient();
  const { toast } = useDialogs();
  const { data, isLoading, error } = useQuery({ queryKey: qk.agentPool, queryFn: api.agentPool });
  const [globalMax, setGlobalMax] = useState("0");
  const [types, setTypes] = useState<AgentPoolType[]>([]);
  const [newType, setNewType] = useState("");

  useEffect(() => {
    if (data) {
      setGlobalMax(String(data.global_max));
      setTypes(data.types);
    }
  }, [data]);

  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.putAgentPool({
        global_max: Number(globalMax),
        types: types.map(({ agent_type, enabled, max_concurrent }) => ({ agent_type, enabled, max_concurrent })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.agentPool });
      toast("Agent pool saved", "info");
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const setType = (i: number, patch: Partial<AgentPoolType>) =>
    setTypes((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  return (
    <div className="form-stack settings-form">
      <p className="muted">Agent slots shared by all projects. Ready tasks are claimed in board order.</p>
      <label className="field field-narrow">
        <span className="field-label">Global max concurrent agents</span>
        <input className="input" type="number" min={0} value={globalMax} onChange={(e) => setGlobalMax(e.target.value)} />
      </label>
      <table className="table">
        <thead>
          <tr>
            <th>Agent type</th>
            <th>Enabled</th>
            <th>Max concurrent</th>
            <th className="num">Running</th>
          </tr>
        </thead>
        <tbody>
          {types.map((t, i) => (
            <tr key={t.agent_type}>
              <td>
                <code className="code-inline">{t.agent_type}</code>
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={t.enabled}
                  onChange={(e) => setType(i, { enabled: e.target.checked })}
                  aria-label={`Enable ${t.agent_type}`}
                />
              </td>
              <td>
                <input
                  className="input input-inline input-num"
                  type="number"
                  min={0}
                  value={t.max_concurrent}
                  onChange={(e) => setType(i, { max_concurrent: Number(e.target.value) })}
                  aria-label={`Max concurrent ${t.agent_type}`}
                />
              </td>
              <td className="num">{t.running}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          const v = newType.trim();
          if (v && !types.some((t) => t.agent_type === v)) {
            setTypes((ts) => [...ts, { agent_type: v, enabled: true, max_concurrent: 1, running: 0 }]);
            setNewType("");
          }
        }}
      >
        <input
          className="input input-mono"
          placeholder="agent type, e.g. claude, codex, mock"
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
        />
        <button className="btn">Add type</button>
      </form>
      {save.error && <div className="error-box">{save.error.message}</div>}
      <div className="row-actions row-actions-start">
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </button>
      </div>
    </div>
  );
}
